import { TFunction } from "i18next";
import { DataError } from "../../core/errors/DataError";
import { enviroment } from "../../enviroment";
import { UPLOAD_REJECTED_ERROR_CODE } from "../../data/models/mappers/mapError";

// "Upload stands alone" (flag REACT_APP_UPLOAD_PENDING_ENABLED, design 14/9
// §4/§5.6) - the pure mapping of the API's deterministic rejection codes
// (LogoDto.uploadFailedReason) to the user-facing texts, and the rule for
// when the rejected FILE may be offered again. The text/prefill helpers read
// no flag: the call sites branch on the flag-gated predicates. The one flag
// read is isUploadRejectedError (the sync path's 422, 28/9) - flag off =
// always false, so withToasts keeps today's toast.

// The codes the API's worker writes (UploadPendingPolicy.ClassifyUpload).
export const UPLOAD_REJECT_SIZE_TOO_BIG = "SizeTooBig";
export const UPLOAD_REJECT_TOO_MANY_ARTBOARDS = "TooManyArtboards";
export const UPLOAD_REJECT_NO_SERIAL = "NoSerial";
// API flag UploadRejectMatchV2Enabled (28/9): app117 answered "SVG file
// missing" / "SVG fil mangler" for a file whose SVG layer it could not find
// (logos 722461, 724698) - retrying the same file never helped. Its exact
// meaning is still an open question to the supplier, so it has NO text key
// of its own: uploadRejectedTextKey gives it the generic text carrying the
// supplier's own words.
export const UPLOAD_REJECT_SVG_MISSING = "SvgMissing";

/**
 * The i18n key of the explanation shown for a rejection code. Unknown codes
 * ("NoOriginal", "Rejected", anything newer than this build) get the
 * generic text, which carries the supplier's own message ({{message}}) -
 * or, when the API sent no message at all, a plain sentence without the
 * dangling colon the generic template would leave (skeptic round 1).
 */
export function uploadRejectedTextKey(
  reason: string | null | undefined,
  message?: string | null
): string {
  switch (reason) {
    case UPLOAD_REJECT_SIZE_TOO_BIG:
      return "upload_rejected_size_too_big";
    case UPLOAD_REJECT_TOO_MANY_ARTBOARDS:
      return "upload_rejected_too_many_artboards";
    case UPLOAD_REJECT_NO_SERIAL:
      return "upload_rejected_no_serial";
    default:
      return message && message.trim()
        ? "upload_rejected_generic"
        : "upload_rejected_unknown";
  }
}

/**
 * Whether "Upload another file" may pre-fill the New logo dialog with the
 * file that was just rejected: only when the FILE is not the problem. A
 * file the supplier found too large or with too many artboards must be
 * replaced by another one; a read failure (no serial) or an unclassified
 * rejection is worth one more try with the same file.
 */
export function canPrefillRejectedFile(reason: string | null | undefined): boolean {
  return (
    reason !== UPLOAD_REJECT_SIZE_TOO_BIG &&
    reason !== UPLOAD_REJECT_TOO_MANY_ARTBOARDS &&
    reason !== UPLOAD_REJECT_SVG_MISSING
  );
}

/**
 * The upload sync path's deterministic rejection: the API's 422
 * { error: "uploadRejected", uploadFailedReason, uploadFailedMessage }
 * (sent only under its UploadRejectMatchV2Enabled), kept by mapError as
 * errorCode "uploadRejected" on a 422 DataError. withToasts shows ONE
 * translated toast for it instead of the compound "Could not create logo:
 * <message>". Flag REACT_APP_UPLOAD_PENDING_ENABLED off = always false.
 */
export function isUploadRejectedError(error: unknown): boolean {
  if (!enviroment.uploadPendingEnabled) return false;
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  return (
    !!status &&
    status.code === 422 &&
    status.errorCode === UPLOAD_REJECTED_ERROR_CODE
  );
}

/**
 * The toast text of an isUploadRejectedError: the same translated text the
 * pending path's dialog/badge show for the code, with the supplier's own
 * (API-cleaned) words for the generic text - never the body's English
 * `message`. Call only for an error that passed isUploadRejectedError.
 */
export function uploadRejectedToastText(error: unknown, t: TFunction): string {
  const status = error instanceof DataError ? error.errorStatus : undefined;
  const reason = status?.uploadFailedReason;
  const message = status?.uploadFailedMessage;
  return t(uploadRejectedTextKey(reason, message), { message: message ?? "" });
}
